import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    location: '',
    service: '',
    addons: [] as string[],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddonChange = (addon: string) => {
    setFormData(prev => ({
      ...prev,
      addons: prev.addons.includes(addon)
        ? prev.addons.filter(item => item !== addon)
        : [...prev.addons, addon]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validate textarea length
    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      setIsSubmitting(false);
      return;
    }

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('location', formData.location);
      formDataToSend.append('service', formData.service);
      formDataToSend.append('addons', formData.addons.join(', '));
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d44mi0hfijdi71nrb2dg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          location: '',
          service: '',
          addons: [],
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addonOptions = [
    'Headlight Restoration',
    'Engine Bay Detail',
    'Paint Decontamination',
    'Bug & Tar Removal',
    'Water Spot Removal',
    'Trim Restoration',
    'Plastic & Rubber Restoration',
    'Wheel Polishing',
    'Exhaust Tip Polish',
    'Pet Hair Removal',
    'Fabric Protection',
    'Odour Removal Treatment',
    'Paint Correction',
    'Windshield Coating'
  ];

  return (
    <section id="contact" className="py-12 sm:py-16 lg:py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          <div className="order-2 lg:order-1">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6">
              <strong>Get Your Quote Today</strong>
            </h3>
            <p className="text-base sm:text-lg text-gray-300 mb-6 sm:mb-8">
              Ready to experience the best mobile car detailing in Melbourne? 
              Contact us for a free quote and let us bring showroom quality to your location.
            </p>

            <div className="space-y-4 sm:space-y-6">
              <a href="tel:0491121950" className="flex items-center p-4 bg-gray-800 rounded-lg shadow-sm hover:bg-gray-700 transition-colors cursor-pointer border border-gray-700">
                <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-phone-line text-teal-400 text-xl"></i>
                </div>
                <div>
                  <div className="font-semibold text-white">Phone</div>
                  <div className="text-teal-400 font-bold text-lg">
                    0491 121 950
                  </div>
                </div>
              </a>

              <div className="flex items-center p-4 bg-gray-800 rounded-lg shadow-sm border border-gray-700">
                <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-map-pin-line text-teal-400 text-xl"></i>
                </div>
                <div>
                  <div className="font-semibold text-white">Service Area</div>
                  <div className="text-gray-400">Melbourne & Surrounding Areas</div>
                </div>
              </div>

              <div className="flex items-center p-4 bg-gray-800 rounded-lg shadow-sm border border-gray-700">
                <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-time-line text-teal-400 text-xl"></i>
                </div>
                <div>
                  <div className="font-semibold text-white">Hours</div>
                  <div className="text-gray-400 text-sm sm:text-base">Mon-Sat: 7AM-6PM | Sun: 9AM-4PM</div>
                </div>
              </div>
            </div>

            <div className="mt-6 sm:mt-8 flex justify-center lg:justify-start space-x-4">
              <a 
                href="https://www.instagram.com/starlightmotors_/" 
                target="_blank" 
                rel="nofollow"
                className="w-12 h-12 bg-teal-500 text-white rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer"
              >
                <i className="ri-instagram-line text-xl"></i>
              </a>
              <a 
                href="https://www.facebook.com/people/Starlightmotors-mobile-detailing/61573939679933/" 
                target="_blank" 
                rel="nofollow"
                className="w-12 h-12 bg-teal-500 text-white rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer"
              >
                <i className="ri-facebook-line text-xl"></i>
              </a>
            </div>
          </div>

          <div className="order-1 lg:order-2 bg-gray-800 rounded-xl shadow-lg p-6 sm:p-8 border border-gray-700">
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base text-white placeholder-gray-400"
                    placeholder="Your full name"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base text-white placeholder-gray-400"
                    placeholder="04 91 121 950"
                  />
                </div>

                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-300 mb-2">
                    Location *
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base text-white placeholder-gray-400"
                    placeholder="Melbourne suburb or address"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-300 mb-2">
                    Service Required *
                  </label>
                  <div className="relative">
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base appearance-none text-white"
                    >
                      <option value="">Select a service</option>
                      <option value="Full Interior & Exterior Detail - $250">Full Interior & Exterior Detail - $250 (Save $30!)</option>
                      <option value="Interior Only Detail - $180">Interior Only Detail - $180 (Save $30!)</option>
                      <option value="Exterior Only Detail - $120">Exterior Only Detail - $120 (Save $30!)</option>
                      <option value="Maintenance Detail - $170">Maintenance Detail - $170</option>
                      <option value="Ceramic Coating - from $600">Ceramic Coating - from $600</option>
                      <option value="Headliner Installation - from $400">Headliner Installation - from $400</option>
                      <option value="Custom Quote Required">Custom Quote Required</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Additional Services (Optional)
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {addonOptions.map((addon) => (
                      <label key={addon} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.addons.includes(addon)}
                          onChange={() => handleAddonChange(addon)}
                          className="w-4 h-4 text-teal-500 bg-gray-700 border-gray-600 rounded focus:ring-teal-500 focus:ring-2"
                        />
                        <span className="ml-2 text-sm text-gray-300">{addon}</span>
                      </label>
                    ))}
                  </div>
                  {formData.addons.length > 0 && (
                    <div className="mt-2 text-xs text-teal-400">
                      Selected: {formData.addons.join(', ')}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base resize-vertical text-white placeholder-gray-400"
                    placeholder="Tell us about your vehicle and any specific requirements..."
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full text-lg py-4"
                  size="lg"
                >
                  {isSubmitting ? 'Sending...' : 'Get Free Quote'}
                </Button>
              </div>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-900/50 border border-green-700 rounded-lg">
                  <div className="flex items-center">
                    <i className="ri-check-circle-line text-green-400 mr-2"></i>
                    <span className="text-green-300">Thank you! We'll contact you within 24 hours.</span>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-900/50 border border-red-700 rounded-lg">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-400 mr-2"></i>
                    <span className="text-red-300">Sorry, there was an error. Please try again or call us directly.</span>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
